import { currentAccessLevel } from './sessionAccess';
import { computeInterest } from './dharaInterest';

export function isBlackLogin() {
  return currentAccessLevel() === 'black';
}

function notebookMonths(startDate, endDate) {
  const s = new Date(startDate);
  const e = new Date(endDate);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  const days = Math.max(
    0,
    360 * (e.getFullYear() - s.getFullYear()) + 30 * (e.getMonth() - s.getMonth()) + (e.getDate() - s.getDate())
  );
  return days / 30;
}

function compoundMonthly(basePrincipal, monthly, months) {
  const P = Number(basePrincipal) || 0;
  const M = Number(monthly) || 0;
  if (P <= 0 || M <= 0 || months <= 0) return 0;
  let balance = P;
  let interest = 0;
  let left = months;
  while (left > 0) {
    const chunk = Math.min(12, left);
    const monthAmount = Math.round((balance * M) / P);
    interest += monthAmount * chunk;
    balance += monthAmount * chunk;
    left -= chunk;
  }
  return interest;
}

/** Illegal quote saved on the black book: F10 monthly rupees, or F1–F5 percentage. */
export function illegalQuote(pledge) {
  const sec = pledge?.secondaryTransaction || pledge?.illegalTransaction;
  const ld = sec?.loanDetails;
  const monthly = Number(ld?.proportionalCompoundMonthlyTotal);
  if (monthly > 0) {
    return {
      kind: 'monthly',
      monthly,
      base: Number(ld?.proportionalCompoundBasePrincipal) || Number(pledge?.loanDetails?.principalAmount) || 0
    };
  }
  const rate = Number(ld?.rateOfInterest);
  if (Number.isFinite(rate) && rate > 0) return { kind: 'rate', rate };
  return null;
}

/** Number for the pledge form rate box. Black login: illegal % or F10 monthly rupees. */
export function blackFormRate(pledge, article) {
  const sec = pledge?.secondaryTransaction || pledge?.illegalTransaction;
  const ld = sec?.loanDetails || {};
  const rate = Number(ld.rateOfInterest);
  if (Number.isFinite(rate) && rate > 0) return String(rate);
  const fromArticles = (sec?.articles || [])
    .map((row) => Number(row.interestRate))
    .find((n) => Number.isFinite(n) && n > 0);
  if (fromArticles) return String(fromArticles);
  const monthly = Number(ld.proportionalCompoundMonthlyTotal) || Number(pledge?.loanDetails?.enteredTotalMonthlyInterest);
  if (monthly > 0) return String(monthly);
  const illegalDefault = Number(article?.defaultIllegalPercentage);
  if (Number.isFinite(illegalDefault) && illegalDefault > 0) return String(illegalDefault);
  return '';
}

export function rateLabel(pledge, black) {
  const legal = Number(pledge?.loanDetails?.rateOfInterest) || 0;
  if (!black) return `${legal}%`;
  const quote = illegalQuote(pledge);
  if (quote?.kind === 'monthly') return `₹${quote.monthly}/mo`;
  if (quote?.kind === 'rate') return `${quote.rate}%`;
  return `${legal}%`;
}

/**
 * Legal login: article legal rate.
 * Black login: F10 monthly total (210), or the F-key percentage.
 * Pass startDate+endDate on release so a monthly contract compounds the same way it is saved.
 */
function calendarDays(startDate, endDate) {
  const s = new Date(startDate);
  const e = new Date(endDate);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  return Math.max(0, Math.ceil((e - s) / (1000 * 60 * 60 * 24)));
}

function proportionalAccrued(basePrincipal, baseMonthly, months) {
  const P = Number(basePrincipal);
  const M = Number(baseMonthly);
  const period = Number(months);
  if (!(P > 0) || !(M > 0) || !(period > 0)) return 0;
  const fullYears = Math.floor(period / 12);
  const remainingMonths = period - fullYears * 12;
  let balance = P;
  for (let y = 0; y < fullYears; y++) {
    const monthly = Math.round((balance * M) / P);
    balance += monthly * 12;
  }
  if (remainingMonths > 0) {
    const monthly = Math.round((balance * M) / P);
    balance += monthly * remainingMonths;
  }
  return Math.round(balance - P);
}

function rateAccrued(principal, rate, illegalDays) {
  const months = illegalDays / 30;
  const fullYears = Math.floor(months / 12);
  const remainingMonths = months - fullYears * 12;
  const r = rate / 100;
  if (fullYears === 0) {
    return Math.round((principal * rate / 100) / 360 * illegalDays);
  }
  const amountAfterFullYears = principal * Math.pow(1 + r, fullYears);
  return Math.round(
    (amountAfterFullYears - principal) + amountAfterFullYears * (r / 12) * remainingMonths
  );
}

function proportionalBases(pledge) {
  const sec = pledge?.secondaryTransaction || pledge?.illegalTransaction;
  const ld = sec?.loanDetails;
  if (ld && Number(ld.proportionalCompoundBasePrincipal) > 0 && Number(ld.proportionalCompoundMonthlyTotal) > 0) {
    return {
      baseP: Number(ld.proportionalCompoundBasePrincipal),
      baseM: Number(ld.proportionalCompoundMonthlyTotal)
    };
  }
  const p = Number(pledge?.loanDetails?.principalAmount);
  const ent = Number(pledge?.loanDetails?.enteredTotalMonthlyInterest);
  if (p > 0 && ent > 0) return { baseP: p, baseM: ent };
  const ro = ld?.rateOfInterest;
  if (ld && p > 0 && (ro === undefined || ro === null)) {
    const legalRate = Number(pledge?.loanDetails?.rateOfInterest);
    const secSlice = Number(ld.monthlyInterestAmount);
    if (Number.isFinite(legalRate) && legalRate >= 0 && secSlice > 0) {
      const inferred = (p * legalRate) / 100 / 12 + secSlice;
      if (inferred > 0) return { baseP: p, baseM: inferred };
    }
  }
  return null;
}

/**
 * Amount posted on the secondary interest ledger at release:
 * secondary interest earned − one month already taken − legal interest.
 * Can be negative (a debit).
 */
export function secondaryReleasePosting(pledge, { startDate, endDate, legalInterest = 0, receipts = [] } = {}) {
  const sec = pledge?.secondaryTransaction || pledge?.illegalTransaction;
  if (!sec || !startDate || !endDate) return 0;

  const principal = Number(pledge?.loanDetails?.principalAmount) || 0;
  const ld = sec.loanDetails || {};
  let paidPrincipal = 0;
  (receipts || []).forEach((receipt) => {
    paidPrincipal += Number(receipt?.interestDetails?.illegalPrincipalAmount) || 0;
  });

  const bases = proportionalBases(pledge);
  const days = calendarDays(startDate, endDate);
  let total = 0;
  let secondaryRate = Number(ld.rateOfInterest);
  let paidInterest = 0;

  if (bases) {
    const baseP = Math.max(0, bases.baseP - paidPrincipal);
    total = proportionalAccrued(baseP, bases.baseM, notebookMonths(startDate, endDate));
    secondaryRate = 0;
    (receipts || []).forEach((receipt) => {
      const id = receipt?.interestDetails || {};
      if (id.type === 'AMOUNT') {
        paidInterest += Number(id.illegalInterestAmount) || 0;
        return;
      }
      paidInterest += Number(id.secondaryTransactionAmount) || Number(id.illegalTransactionAmount) || 0;
    });
  } else {
    if (!Number.isFinite(secondaryRate) || secondaryRate <= 0) secondaryRate = 30;
    const principalForIllegal = Math.max(0, principal - paidPrincipal);
    const illegalDays = days > 0 ? Math.ceil(days / 30) * 30 : 0;
    total = rateAccrued(principalForIllegal, secondaryRate, illegalDays);
    (receipts || []).forEach((receipt) => {
      const id = receipt?.interestDetails || {};
      if (id.type === 'AMOUNT') {
        paidInterest += Number(id.illegalInterestAmount) || 0;
        return;
      }
      const receiptMonths = id.months || 0;
      if (receiptMonths > 0) {
        paidInterest += Math.round((principalForIllegal * secondaryRate / 100) / 360 * receiptMonths * 30);
      } else {
        paidInterest += Number(id.secondaryTransactionAmount) || Number(id.illegalTransactionAmount) || 0;
      }
    });
  }

  const storedAdvance = Number(ld.monthlyInterestAmount);
  const advance = Number.isFinite(storedAdvance) && storedAdvance > 0
    ? storedAdvance
    : (!bases && secondaryRate > 0 ? Math.round((principal * secondaryRate) / 100 / 12) : 0);

  return total - paidInterest - advance - (Number(legalInterest) || 0);
}

export function interestForSession(pledge, { actualDays = 0, dhara, black, startDate, endDate } = {}) {
  const principal = Number(pledge?.loanDetails?.principalAmount || pledge?.principal) || 0;
  const legalRate = Number(pledge?.loanDetails?.rateOfInterest || pledge?.rateOfInterest) || 0;
  if (!black) return computeInterest(principal, legalRate, actualDays, dhara).interest;
  const quote = illegalQuote(pledge);
  if (quote?.kind === 'monthly') {
    if (startDate && endDate) return compoundMonthly(quote.base || principal, quote.monthly, notebookMonths(startDate, endDate));
    return Math.round(quote.monthly * ((Number(actualDays) || 0) / 30));
  }
  if (quote?.kind === 'rate') return computeInterest(principal, quote.rate, actualDays, dhara).interest;
  return computeInterest(principal, legalRate, actualDays, dhara).interest;
}
